import React from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { media } from 'styled-bootstrap-grid';

import Error from '@common/components/Error';
import RequiredIndicator from './styled/RequiredIndicator';
import InputWrapper from './styled/FieldWrapper';
import asReduxFormField from './asReduxFormField';

const CheckboxWrapper = styled.div`
  width: 100%;
  label a {
    font-weight: ${({ theme }) => theme.fontWeights.semiBold};
    color: inherit;
  }

  ${media.md`
    display: flex;
  `}
`;

const StyledCheckbox = styled.input`
  display: none;

  & + label {
    position: relative;
    cursor: pointer;
    padding: 0;
    display: inline-block;
    max-width: 100%;
    overflow: hidden;
    line-height: 20px;
    margin-bottom: 5px;
  }

  & + label:before {
    content: '';
    margin-right: 10px;
    display: inline-block;
    vertical-align: middle;
    width: 20px;
    height: 20px;
    background: white;
    border: 1px solid #1f1f1f;
    border-radius: 3px;
  }

  &:hover + label:before {
    border-color: #1f1f1f;
  }

  &:checked + label:before {
    border-color: #1f1f1f;
    border-radius: 3px;
  }

  &:disabled + label {
    cursor: auto;
  }

  &:disabled + label:before {
    box-shadow: none;
    opacity: 0.5;
  }

  &:checked + label:after {
    content: '';
    position: absolute;
    left: 7px;
    top: 4px;
    width: 7px;
    height: 14px;
    border: solid black;
    border-width: 0 2px 2px 0;
    transform: rotate(45deg);
  }
`;

const StyledError = styled.div`
  display: flex;
  align-items: center;
  color: #f80606;
  flex-shrink: 0;
  font-size: 14px;
  padding: 0;

  & > div {
    margin-top: 0;
    margin-bottom: 5px;
  }

  ${media.lg`
    padding: 0 0 0 10px;
  `}
`;

// Label should be placed next to checbox instead of 1st grid column
// like in the rest of form components
const StyledDummyLabel = styled.span`
  flex-shrink: 0;
  font-weight: ${({ theme }) => theme.fontWeights.semiBold};
  line-height: 24px;

  ${media.lg`
    max-width: ${({ width }) => width};
    width: 100%;
  `}
`;

const AdditionalDescription = styled.div`
  font-size: 10px;
  margin-left: 30px;
  line-height: 12px;
`;

const PureCheckbox = ({
  id,
  isRequired = false,
  label = null,
  labelWidth = '100px',
  requiredLabelWidth = '20%',
  error = null,
  value,
  onChange,
  additionalDescription = '',
  ...props
}) => (
  <InputWrapper>
    <StyledDummyLabel width={labelWidth} />
    {isRequired &&
      RequiredIndicator({
        requiredLabelWidth,
        isRequired,
        desktopOnly: false,
        mobileOnly: true,
      })}
    <CheckboxWrapper>
      <StyledCheckbox id={id} type="checkbox" checked={value} onChange={onChange} {...props} />
      <label htmlFor={id}>
        {label}
        {additionalDescription && (
          <AdditionalDescription>{additionalDescription}</AdditionalDescription>
        )}
      </label>

      {!!error && (
        <StyledError>
          <Error error={error} />
        </StyledError>
      )}
    </CheckboxWrapper>
    {!error && RequiredIndicator({ requiredLabelWidth, isRequired, desktopOnly: true })}
  </InputWrapper>
);

PureCheckbox.propTypes = {
  id: PropTypes.string.isRequired,
  labelWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  requiredLabelWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  isRequired: PropTypes.bool,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  value: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]).isRequired,
  onChange: PropTypes.func.isRequired,
  additionalDescription: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
};

export const ReduxFormCheckbox = asReduxFormField(PureCheckbox);

export default PureCheckbox;
